import type { ComponentProps } from "react";

import { cn } from "@/lib/utils";

/**
 * A card is a claim that the things inside it belong together. On a canvas one
 * shade below white it needs no border to read as a card, so it carries a
 * hairline and the faintest lift instead of an outline.
 *
 * Cards do not nest. If something inside wants its own card, it wants its own
 * section.
 */
export function Card({
  className,
  inset = false,
  ...props
}: ComponentProps<"section"> & { inset?: boolean }) {
  return (
    <section
      className={cn(
        "rounded-card bg-surface-raised",
        inset ? "border-line border" : "shadow-[var(--shadow-lift)]",
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: ComponentProps<"header">) {
  return (
    <header
      className={cn("flex items-start justify-between gap-4 px-5 pt-4 pb-3", className)}
      {...props}
    />
  );
}

export function CardTitle({ className, ...props }: ComponentProps<"h2">) {
  return <h2 className={cn("text-title-3 text-heading", className)} {...props} />;
}

export function CardDescription({ className, ...props }: ComponentProps<"p">) {
  return <p className={cn("text-fg-muted text-subhead mt-1", className)} {...props} />;
}

export function CardBody({ className, ...props }: ComponentProps<"div">) {
  return <div className={cn("px-5 pt-1 pb-5", className)} {...props} />;
}

/** A rule between two parts of the same card. Full-bleed, so it reads as a fold. */
export function CardSeparator({ className, ...props }: ComponentProps<"hr">) {
  return <hr className={cn("border-line border-0 border-t", className)} {...props} />;
}
